/**
 * GJESTELINJA (sak 232, T5): «Du ser det {organisasjon} har delt med deg», og «Tilbake».
 *
 * Står så lenge fanen er i gjestemodus. Den er grunnen til at menyen kan være nesten tom uten at det
 * ser ut som en feil: siden sier hvor man er og hvem som eier det man ser, og veien ut er én knapp.
 * «Tilbake» går dit man kom fra (tråden, kalenderen, oppgavene) — ellers Innboks.
 *
 * ET BÅND ØVERST OVER HELE SKALLET, I NAVIGASJONSLAGET (P10, funn D; Glass 2 §16.3). Den sto øverst
 * i innholdet, og ressurspanelet (en skuff over hele høyden) dekket «Tilbake»: målt 30. september,
 * `elementFromPoint` midt på knappen var panelet, og et klikk ventet til det var lukket. Nå står den
 * over dialoglaget (z-60, som toastene), `Modal` begynner under den og regner ikke et trykk her som
 * et klikk utenfor. `AppShell` legger den over sidemenyen og toppfeltet.
 */
import type { MeResponse } from '@tvitvi/shared';
import { useRouter } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { getGuestReturnTo } from '@/auth/guest-org';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';

import { useGuestMode } from './guest-mode';

export function GuestBar({ me }: { me: MeResponse }): ReactNode {
  const { t } = useTranslation();
  const router = useRouter();
  const { leave } = useGuestMode();
  const [leaving, setLeaving] = useState(false);

  const organization =
    me.guestOrganizations.find((one) => one.organizationId === me.activeOrganizationId)
      ?.organizationName ?? '';

  return (
    <div
      role="region"
      aria-label={t('guest.bar.label')}
      data-testid="guest-bar"
      data-guest-bar=""
      /* Radix setter `pointer-events: none` på `<body>` mens et panel er åpent — også inline. */
      style={{ pointerEvents: 'auto' }}
      className={cn(
        'glass-nav relative z-[60] mx-[var(--shell-gap)] mt-[var(--shell-gap)]',
        'flex h-[var(--topbar-height)] shrink-0 items-center justify-between gap-3',
        'rounded-[var(--radius-bar)] px-4',
      )}
    >
      <p className="min-w-0 truncate text-sm text-default">
        {t('guest.bar.text', { organization })}
      </p>
      <Button
        variant="secondary"
        size="sm"
        disabled={leaving}
        data-testid="guest-bar-back"
        onClick={() => {
          const to = getGuestReturnTo() ?? '/inbox';
          setLeaving(true);
          void leave()
            .then(() => router.history.push(to))
            .finally(() => setLeaving(false));
        }}
      >
        {t('guest.bar.back')}
      </Button>
    </div>
  );
}
